import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useGlobal } from './GlobalContext';

const DeployContext = createContext();

export const useDeploy = () => {
  const context = useContext(DeployContext);
  if (!context) {
    throw new Error('useDeploy must be used within a DeployProvider');
  }
  return context;
};

export const DeployProvider = ({ children }) => {
  const { apiKey, accountId, baseServiceUrl } = useGlobal();

  const apiCall = useCallback(
    async (endpoint, options = {}) => {
      if (!apiKey) {
        throw new Error('API key is required');
      }

      const url = `${baseServiceUrl}${endpoint}`;
      const defaultOptions = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
          ...options.headers,
        },
      };
      if (apiKey) {
        defaultOptions.headers['x-api-key'] = apiKey;
      }
      if (accountId) {
        defaultOptions.headers['x-account'] = accountId;
      }
      const response = await fetch(url, { ...defaultOptions, ...options });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody.error || `API call failed: ${response.status} ${response.statusText}`
        );
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    },
    [apiKey, accountId, baseServiceUrl]
  );

  const deployApi = useMemo(
    () => ({
      listFeedAlgos: async (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.account_id) searchParams.set('account_id', params.account_id);
        if (params.user_id) searchParams.set('user_id', params.user_id);
        const query = searchParams.toString();
        const endpoint = `/deploy/algos${query ? `?${query}` : ''}`;
        const result = await apiCall(endpoint);
        return result;
      },
      getFeedAlgo: async (id) => {
        const result = await apiCall(`/deploy/algos/${id}`);
        return result;
      },
      createFeedAlgo: async (data) => {
        const result = await apiCall('/deploy/algos', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        return result;
      },
      updateFeedAlgo: async (id, data) => {
        const result = await apiCall(`/deploy/algos/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
        return result;
      },
      deleteFeedAlgo: async (id) => {
        await apiCall(`/deploy/algos/${id}`, { method: 'DELETE' });
      },
      listFeedConfigs: async (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.account_id) searchParams.set('account_id', params.account_id);
        if (params.user_id) searchParams.set('user_id', params.user_id);
        const query = searchParams.toString();
        const endpoint = `/deploy/configs${query ? `?${query}` : ''}`;
        const result = await apiCall(endpoint);
        return result;
      },
      getFeedConfig: async (id) => {
        const result = await apiCall(`/deploy/configs/${id}`);
        return result;
      },
      createFeedConfig: async (data) => {
        const result = await apiCall('/deploy/configs', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        return result;
      },
      updateFeedConfig: async (id, data) => {
        const result = await apiCall(`/deploy/configs/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
        return result;
      },
      deleteFeedConfig: async (id) => {
        await apiCall(`/deploy/configs/${id}`, { method: 'DELETE' });
      },
      servePreview: async (config, inputs = {}) => {
        const result = await apiCall('/deploy/preview', {
          method: 'POST',
          body: JSON.stringify({ config, inputs }),
        });
        return result;
      },
      serve: async (configId, inputs = {}) => {
        const result = await apiCall('/deploy/serve', {
          method: 'POST',
          body: JSON.stringify({ config_id: configId, inputs }),
        });
        return result;
      },
      serveFeedPage: async (servedFeedId, pageNum) => {
        const params = new URLSearchParams({
          served_feed_id: servedFeedId,
          page_num: String(pageNum),
        });
        const result = await apiCall(`/deploy/serve/page?${params}`);
        return result;
      },
      serveNewItems: async (servedFeedId) => {
        const params = new URLSearchParams({ served_feed_id: servedFeedId });
        const result = await apiCall(`/deploy/serve/new_items?${params}`);
        return result;
      },
      clearCache: async (feedIds, inputs = {}) => {
        const result = await apiCall('/deploy/cache/clear', {
          method: 'POST',
          body: JSON.stringify({ feedIds, inputs }),
        });
        return result;
      },
      ingests: {
        list: async () => {
          const result = await apiCall('/deploy/ingests');
          return result;
        },
        get: async (id) => {
          const result = await apiCall(`/deploy/ingests/${id}`);
          return result;
        },
        create: async (data) => {
          const result = await apiCall('/deploy/ingests', {
            method: 'POST',
            body: JSON.stringify(data),
          });
          return result;
        },
        update: async (id, data) => {
          const result = await apiCall(`/deploy/ingests/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
          });
          return result;
        },
        delete: async (id) => {
          await apiCall(`/deploy/ingests/${id}`, { method: 'DELETE' });
        },
        activate: async (id) => {
          const result = await apiCall(`/deploy/ingests/${id}/activate`, { method: 'POST' });
          return result;
        },
        pause: async (id) => {
          const result = await apiCall(`/deploy/ingests/${id}/pause`, { method: 'POST' });
          return result;
        },
      },
      notifications: {
        list: async () => {
          const result = await apiCall('/deploy/notifications');
          return result;
        },
        get: async (id) => {
          const result = await apiCall(`/deploy/notifications/${id}`);
          return result;
        },
        create: async (data) => {
          const result = await apiCall('/deploy/notifications', {
            method: 'POST',
            body: JSON.stringify(data),
          });
          return result;
        },
        update: async (id, data) => {
          const result = await apiCall(`/deploy/notifications/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
          });
          return result;
        },
        delete: async (id) => {
          await apiCall(`/deploy/notifications/${id}`, { method: 'DELETE' });
        },
        activate: async (id) => {
          const result = await apiCall(`/deploy/notifications/${id}/activate`, { method: 'POST' });
          return result;
        },
        pause: async (id) => {
          const result = await apiCall(`/deploy/notifications/${id}/pause`, { method: 'POST' });
          return result;
        },
        preview: async (id, { limit = 100 } = {}) => {
          const result = await apiCall(`/deploy/notifications/${id}/preview?limit=${limit}`);
          return result;
        },
        test: async (id) => {
          const result = await apiCall(`/deploy/notifications/${id}/test`, { method: 'POST' });
          return result;
        },
      },
    }),
    [apiCall]
  );

  const deploySteps = useMemo(
    () => [
      { label: 'Algos', enabled: true },
      { label: 'Configs', enabled: true },
      { label: 'Notifications', enabled: true },
      { label: 'Warmup', enabled: true },
    ],
    []
  );

  const value = {
    deployApi,
    deploySteps,
  };

  return (
    <DeployContext.Provider value={value}>
      {children}
    </DeployContext.Provider>
  );
};
